import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useContext } from "react";
import { ProjectsContext } from "../admin/ProjectsContext";

import {
  Sparkles,
  Building2,
  Leaf,
  TreePine,
  Wheat,
  CheckCircle2,
} from "lucide-react";

const highlightIconsByCategory = {
  residential: Sparkles,
  farmland: Leaf,
};

const amenityIconsByCategory = {
  residential: Building2,
  farmland: TreePine,
};

const iconStyle = "text-brand-gold mt-0.5 flex-shrink-0";
const ProjectDetail = ({ onBookingClick }) => {
  const { projects } = useContext(ProjectsContext);
  const { id } = useParams();

  if (!projects || projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500 text-lg">Loading project...</p>
      </div>
    );
  }

  const project = projects.find((p) => String(p.id) === id);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-brand-dark mb-4">
            Project Not Found
          </h2>
          <Link to="/projects">
            <Button className="btn-primary">Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + project.images.length) % project.images.length
    );
  };
  const getGoogleMapsLinkFromEmbed = (embedUrl) => {
    if (!embedUrl) return null;

    // Extract place name or coordinates fallback
    return embedUrl
      .replace(
        "https://www.google.com/maps/embed?",
        "https://www.google.com/maps?"
      )
      .replace("pb=", "");
  };

  <CheckCircle2 size={18} className="text-brand-gold mt-0.5 flex-shrink-0" />;

  const generateBrochure = async () => {
    const element = document.getElementById("brochure-content");

    if (!element) {
      alert("Brochure content not found");
      return;
    }
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      windowWidth: document.body.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${project.name}-Brochure.pdf`);
  };

  return (
    <div className="min-h-screen py-20" id="brochure-content">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-brand-dark mb-3">
            {project.name}
          </h1>
          <p className="text-xl text-gray-600 mb-4">{project.tagline}</p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-gray-600 pdf-hide">
              <MapPin size={20} className="text-brand-gold" />
              <span>{project.location}</span>
            </div>
            <div className="bg-brand-gold text-white px-4 py-2 rounded-full font-semibold">
              {project.status}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="mb-8">
              <div className="relative h-96 md:h-[500px] rounded-xl overflow-hidden mb-4">
                <img
                  src={project.images[currentImageIndex]}
                  alt={`${project.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setShowLightbox(true)}
                />
                {project.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-3 gap-3">
                {project.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`h-24 md:h-32 w-full object-cover rounded-lg cursor-pointer transition-all duration-300 ${
                      index === currentImageIndex
                        ? "ring-4 ring-brand-gold"
                        : "hover:ring-2 ring-gray-300"
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            </div>

            {/* Description */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-brand-dark mb-4">
                  About This Project
                </h2>
                <p className="text-gray-700 leading-relaxed">{project.about}</p>
              </CardContent>
            </Card>
            {/* Why Choose This Project */}
            {/* Why Choose This Project */}
            {project.whyChoose?.length > 0 && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-brand-dark mb-4">
                    Why Choose {project.name}?
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.whyChoose.map((point, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2
                          size={18}
                          className="text-brand-gold mt-0.5 flex-shrink-0"
                        />
                        <span className="text-gray-700 leading-relaxed">
                          {point}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Highlights */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-brand-dark mb-4">
                  Key Highlights
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.highlights.map((highlight, index) => {
                    const Icon =
                      highlightIconsByCategory[project.category] || Sparkles;

                    return (
                      <div key={index} className="flex items-start gap-3">
                        <Icon size={18} className={iconStyle} />
                        <span className="text-gray-700 leading-relaxed">
                          {highlight}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-brand-dark mb-4">
                  Amenities
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.amenities?.map((item, index) => {
                    const Icon =
                      amenityIconsByCategory[project.category] || Building2;

                    return (
                      <div key={index} className="flex items-start gap-3">
                        <Icon size={18} className={iconStyle} />
                        <span className="text-gray-700 leading-relaxed">
                          {item}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Payment Schedule (if available) */}
            {project.paymentSchedule && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-brand-dark mb-4">
                    Payment Schedule
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-brand-gold">
                          <th className="text-left py-3 px-4 font-bold text-brand-dark">
                            Milestone
                          </th>
                          <th className="text-right py-3 px-4 font-bold text-brand-dark">
                            Percentage
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.paymentSchedule.map((item, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-3 px-4 text-gray-700">
                              {item.milestone}
                            </td>
                            <td className="py-3 px-4 text-right text-brand-gold font-semibold">
                              {item.percentage}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Location (PDF Friendly) */}
            {/* Location (PDF Friendly) */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-brand-dark mb-4">
                  Location
                </h2>

                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="text-brand-gold mt-1" />
                  <p className="text-gray-700">{project.location}</p>
                </div>

                {project.mapEmbedUrl && (
                  <p className="text-sm text-gray-700 break-all pdf-only">
                    <span className="font-semibold">Google Maps Link: </span>
                    <a
                      href={getGoogleMapsLinkFromEmbed(project.mapEmbedUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-gold underline"
                    >
                      {getGoogleMapsLinkFromEmbed(project.mapEmbedUrl)}
                    </a>
                  </p>
                )}
                {/* Location Advantages */}
                {project.locationAdvantages?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-brand-dark mb-3">
                      Location Advantages
                    </h3>

                    <ul className="space-y-2">
                      {project.locationAdvantages.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-brand-gold rounded-full mt-2"></span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map */}
            {/* Map (Website only) */}
            {/* Map (Website only) */}
            <Card className="pdf-hide">
              <CardContent className="p-6">
                {/* NO Location heading here */}
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    src={project.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    title="Project Location"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* Price Card */}
              <Card className="mb-6">
                <CardContent className="p-0">
                  <Button
                    onClick={() => onBookingClick(project.name)}
                    className="w-full px-12 py-6 text-xl font-semibold rounded-lg btn-primary"
                  >
                    Book Site Visit
                  </Button>
                </CardContent>
              </Card>

              {/* Project Info */}
              {/* Project Info */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-brand-dark mb-4">
                    Project Information
                  </h3>

                  <div className="space-y-3">
                    {project.projectInfo &&
                      Object.entries(project.projectInfo).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className="grid grid-cols-[180px_1fr] text-gray-700"
                          >
                            <span className="font-medium">{key}</span>
                            <span className="text-left">{value}</span>
                          </div>
                        )
                      )}
                  </div>
                </CardContent>
              </Card>

              {/* Approvals */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-brand-dark mb-4">
                    Approvals
                  </h3>
                  <div className="space-y-2">
                    {project.approvals.filter((a) => a !== "Clear Title")
                      .length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.approvals
                          .filter((a) => a !== "Clear Title")
                          .map((approval, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium"
                            >
                              {approval}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          <img
            src={project.images[currentImageIndex]}
            alt={project.name}
            className="max-h-[90vh] max-w-full object-contain"
          />
          {project.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
